import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/firebase/authorityClient";
import { isOwnWalletSummariesFeatureEnabled } from "@/config/runtimeConfig";
import {
  UNIT_AMOUNT_SCALE,
  isCanonicalNonnegativeAtomic,
} from "@/utils/atomicCurrencyFormatting";
import { withUiDeadline } from "@/utils/uiDeadline";

const PAGE_SIZE = 50;
const CACHE_TTL_MS = 15_000;
const ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const HEX_COLOUR_PATTERN = /^#[0-9A-F]{6}$/;
const CURRENCY_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._-]{0,15}$/;
const ASSET_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const CURSOR_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;
const WALLET_STATUSES = new Set(["ready", "setup-pending", "invalid"]);
const APPEARANCE_SOURCES = new Set([
  "personal",
  "organisation",
  "unit-rainbow",
]);
const SUMMARY_KEYS = Object.freeze([
  "schemaVersion",
  "orgId",
  "name",
  "logo",
  "currency",
  "walletStatus",
  "balances",
  "accentSeed",
  "appearanceSource",
]);
const BALANCE_KEYS = Object.freeze([
  "amountScale",
  "currentAvailableAtomic",
  "savingsAvailableAtomic",
  "totalAvailableAtomic",
]);
const PAGE_KEYS = Object.freeze([
  "schemaVersion",
  "activeSummary",
  "summaries",
  "nextCursor",
  "hasMore",
]);

export class WalletSummaryServiceError extends Error {
  constructor(code = "unknown") {
    super("Organisation wallets could not be loaded.");
    this.name = "WalletSummaryServiceError";
    this.code = code;
  }
}

function fail(code) {
  throw new WalletSummaryServiceError(code);
}

function requireActor(authInstance) {
  const actor = authInstance?.currentUser;
  if (
    !actor ||
    typeof actor.uid !== "string" ||
    actor.uid.length < 1 ||
    actor.uid.length > 128 ||
    CONTROL_CHARACTERS.test(actor.uid)
  ) {
    fail("unauthenticated");
  }
  return actor;
}

function hasExactKeys(value, expectedKeys) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return false;
  const actualKeys = Object.keys(value).sort();
  const sortedExpectedKeys = [...expectedKeys].sort();
  return (
    actualKeys.length === sortedExpectedKeys.length &&
    actualKeys.every((key, index) => key === sortedExpectedKeys[index])
  );
}

function normalizeCurrency(value) {
  if (!hasExactKeys(value, ["name", "symbol", "code"])) {
    fail("invalid-response");
  }
  const { name, symbol, code } = value;
  if (
    typeof name !== "string" ||
    name !== name.trim() ||
    name.length < 1 ||
    name.length > 40 ||
    CONTROL_CHARACTERS.test(name) ||
    typeof symbol !== "string" ||
    symbol !== symbol.trim() ||
    symbol.length < 1 ||
    symbol.length > 16 ||
    Array.from(symbol).length > 8 ||
    CONTROL_CHARACTERS.test(symbol) ||
    typeof code !== "string" ||
    !CURRENCY_CODE_PATTERN.test(code)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({ name, symbol, code });
}

function normalizeBalances(value, walletStatus) {
  if (walletStatus !== "ready") {
    if (value !== null) fail("invalid-response");
    return null;
  }
  if (!hasExactKeys(value, BALANCE_KEYS) || value.amountScale !== UNIT_AMOUNT_SCALE) {
    fail("invalid-response");
  }
  const currentAvailableAtomic = value.currentAvailableAtomic;
  const savingsAvailableAtomic = value.savingsAvailableAtomic;
  const totalAvailableAtomic = value.totalAvailableAtomic;
  if (
    !isCanonicalNonnegativeAtomic(currentAvailableAtomic) ||
    !isCanonicalNonnegativeAtomic(savingsAvailableAtomic) ||
    !isCanonicalNonnegativeAtomic(totalAvailableAtomic) ||
    BigInt(currentAvailableAtomic) + BigInt(savingsAvailableAtomic) !==
      BigInt(totalAvailableAtomic)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    amountScale: UNIT_AMOUNT_SCALE,
    currentAvailableAtomic,
    savingsAvailableAtomic,
    totalAvailableAtomic,
  });
}

export function normalizeWalletSummary(value) {
  if (!hasExactKeys(value, SUMMARY_KEYS)) {
    fail("invalid-response");
  }
  const orgId = value.orgId;
  const name = value.name;
  const accentSeed =
    typeof value.accentSeed === "string" ? value.accentSeed.toUpperCase() : null;
  const appearanceSource = value.appearanceSource;

  if (
    value.schemaVersion !== 1 ||
    typeof orgId !== "string" ||
    !ORG_ID_PATTERN.test(orgId) ||
    typeof name !== "string" ||
    name !== name.trim() ||
    name.length < 2 ||
    name.length > 80 ||
    CONTROL_CHARACTERS.test(name) ||
    !WALLET_STATUSES.has(value.walletStatus) ||
    !APPEARANCE_SOURCES.has(appearanceSource) ||
    accentSeed === null ||
    accentSeed !== value.accentSeed ||
    !HEX_COLOUR_PATTERN.test(accentSeed) ||
    (value.logo !== null &&
      (typeof value.logo !== "string" || !ASSET_ID_PATTERN.test(value.logo)))
  ) {
    fail("invalid-response");
  }

  return Object.freeze({
    schemaVersion: 1,
    orgId,
    name,
    // This is a private media asset identifier, not a browser-safe URL. Keep
    // Avatar on initials until the response contract provides a signed URL.
    logo: null,
    currency: normalizeCurrency(value.currency),
    balances: normalizeBalances(value.balances, value.walletStatus),
    walletStatus: value.walletStatus,
    accentSeed,
    appearanceSource,
  });
}

function normalizeCursor(value) {
  if (value === null) return null;
  if (
    typeof value !== "string" ||
    value.length < 32 ||
    value.length > 2048 ||
    !CURSOR_PATTERN.test(value)
  ) {
    fail("invalid-argument");
  }
  return value;
}

function normalizeListOptions(value) {
  const options = value === undefined ? {} : value;
  const prototype =
    options && typeof options === "object"
      ? Object.getPrototypeOf(options)
      : undefined;
  if (
    !options ||
    typeof options !== "object" ||
    Array.isArray(options) ||
    (prototype !== Object.prototype && prototype !== null) ||
    Object.keys(options).some(
      (key) => !["cursor", "activeOrgId"].includes(key),
    )
  ) {
    fail("invalid-argument");
  }
  const cursor = normalizeCursor(options.cursor ?? null);
  const activeOrgId = options.activeOrgId ?? null;
  if (
    activeOrgId !== null &&
    (typeof activeOrgId !== "string" || !ORG_ID_PATTERN.test(activeOrgId))
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({ cursor, activeOrgId });
}

function normalizePage(value, request) {
  if (
    !hasExactKeys(value, PAGE_KEYS) ||
    value.schemaVersion !== 1 ||
    !Array.isArray(value.summaries) ||
    value.summaries.length > PAGE_SIZE ||
    typeof value.hasMore !== "boolean" ||
    (value.nextCursor !== null &&
      (typeof value.nextCursor !== "string" ||
        value.nextCursor.length < 32 ||
        value.nextCursor.length > 2048 ||
        !CURSOR_PATTERN.test(value.nextCursor))) ||
    value.hasMore !== (value.nextCursor !== null) ||
    (value.hasMore && value.summaries.length !== PAGE_SIZE) ||
    (value.activeSummary !== null &&
      (!value.activeSummary ||
        typeof value.activeSummary !== "object" ||
        Array.isArray(value.activeSummary)))
  ) {
    fail("invalid-response");
  }

  const summaries = value.summaries.map(normalizeWalletSummary);
  const activeSummary =
    value.activeSummary === null
      ? null
      : normalizeWalletSummary(value.activeSummary);

  const summaryOrgIds = summaries.map(({ orgId }) => orgId);
  if (
    new Set(summaryOrgIds).size !== summaryOrgIds.length ||
    (request.activeOrgId === null
      ? activeSummary !== null
      : activeSummary?.orgId !== request.activeOrgId) ||
    (activeSummary !== null && summaryOrgIds.includes(activeSummary.orgId)) ||
    (request.cursor !== null && value.nextCursor === request.cursor)
  ) {
    fail("invalid-response");
  }

  return Object.freeze({
    activeSummary,
    summaries: Object.freeze(summaries),
    nextCursor: value.nextCursor,
    hasMore: value.hasMore,
  });
}

export function createWalletSummaryService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  enabled = isOwnWalletSummariesFeatureEnabled,
  now = () => Date.now(),
} = {}) {
  let listCallable;
  let exactCallable;
  let cacheGeneration = 0;
  const cache = new Map();

  const cached = async (key, actor, load) => {
    const existing = cache.get(key);
    const currentTime = now();
    if (
      existing?.actor === actor &&
      existing.value !== undefined &&
      existing.expiresAt > currentTime
    ) {
      return existing.value;
    }
    if (existing?.actor === actor && existing.promise) return existing.promise;

    const requestGeneration = cacheGeneration;
    const promise = load()
      .then((value) => {
        if (
          authInstance?.currentUser === actor &&
          cacheGeneration === requestGeneration &&
          cache.get(key)?.promise === promise
        ) {
          cache.set(key, {
            actor,
            value,
            expiresAt: now() + CACHE_TTL_MS,
          });
        } else if (cache.get(key)?.promise === promise) {
          cache.delete(key);
        }
        return value;
      })
      .catch((error) => {
        if (cache.get(key)?.promise === promise) cache.delete(key);
        throw error;
      });
    cache.set(key, { actor, promise, expiresAt: 0 });
    return promise;
  };

  return Object.freeze({
    clearCache() {
      cacheGeneration += 1;
      cache.clear();
    },

    async getOwnWalletSummary(orgId) {
      if (!enabled) fail("feature-disabled");
      if (typeof orgId !== "string" || !ORG_ID_PATTERN.test(orgId)) {
        fail("invalid-argument");
      }
      const expectedActor = requireActor(authInstance);
      if (!exactCallable) {
        exactCallable = callableFactory(functionsInstance, "getOwnWalletSummary");
      }
      const key = `exact:${expectedActor.uid}:${orgId}`;
      return cached(key, expectedActor, async () => {
        try {
          const response = await withUiDeadline(
            exactCallable({ schemaVersion: 1, orgId }),
            { phase: "active organisation Wallet" },
          );
          if (authInstance?.currentUser !== expectedActor) {
            fail("stale-auth-context");
          }
          return normalizeWalletSummary(response?.data);
        } catch (error) {
          if (error instanceof WalletSummaryServiceError) throw error;
          const code = String(error?.code || "unknown").split("/").pop();
          throw new WalletSummaryServiceError(code);
        }
      });
    },

    async listOwnWalletSummaries(options = {}) {
      if (!enabled) fail("feature-disabled");
      const request = normalizeListOptions(options);
      const expectedActor = requireActor(authInstance);
      if (!listCallable) {
        listCallable = callableFactory(functionsInstance, "listOwnWalletSummaries");
      }

      const key = `list:${expectedActor.uid}:${request.activeOrgId || ""}:${request.cursor || ""}`;
      return cached(key, expectedActor, async () => {
        try {
          const response = await withUiDeadline(
            listCallable({
              schemaVersion: 1,
              cursor: request.cursor,
              limit: PAGE_SIZE,
              activeOrgId: request.activeOrgId,
            }),
            { phase: "organisation wallets" },
          );
          if (authInstance?.currentUser !== expectedActor) {
            fail("stale-auth-context");
          }
          return normalizePage(response?.data, request);
        } catch (error) {
          if (error instanceof WalletSummaryServiceError) throw error;
          const code = String(error?.code || "unknown").split("/").pop();
          throw new WalletSummaryServiceError(code);
        }
      });
    },
  });
}

export const walletSummaryService = createWalletSummaryService();
export { CACHE_TTL_MS, PAGE_SIZE };
